import { ExternalLinkIcon } from "@chakra-ui/icons";
import { Link } from "@chakra-ui/react";
import { useIntl } from "react-intl";

import { AnimateSize } from "@nens/chakra/components";
import { RanaButton } from "@nens/ranalib";
import { Simulation, Usage } from "@nens/threedi/openapi-client";

import { DetailRow, DetailTable } from "@/components/detail-table";
import { HeaderBox } from "@/components/header-box";
import { useHccRevisionSimulationsQuery } from "@/queries/query-hcc-simulations";
import { useHccModelSimulationsUsagesQuery } from "@/queries/query-hcc-usage";
import { getPrettyDateTime } from "@/utils/get-pretty-time";

import { useModelSchematisationPermission } from "../hooks/use-model-schematisation-permission";
import { useSchematisationDetailsContext } from "../schematisation-details-context";

function SimulationLink({ simulationId, label }: { simulationId: number; label: string }) {
  return (
    <RanaButton
      as={Link}
      href={`/hcc-management/simulations/${simulationId}`}
      isExternal
      variant="tertiary"
      size="sm"
      rightIcon={<ExternalLinkIcon />}
    >
      {label}
    </RanaButton>
  );
}

function getModelSimulationRows(
  usages: Usage[] | undefined,
  hccPageLabel: string,
  canEditModelSchema: boolean
): DetailRow[] {
  return (usages ?? []).flatMap((usage) => {
    if (!usage.simulation || usage.simulation.id === undefined) return [];

    return [
      {
        id: String(usage.simulation.id),
        name: usage.simulation.name,
        value: usage.started ? getPrettyDateTime(usage.started) : null,
        ...(canEditModelSchema && {
          action: <SimulationLink simulationId={usage.simulation.id} label={hccPageLabel} />,
        }),
      },
    ];
  });
}

function getRevisionSimulationRows(
  simulations: Simulation[] | undefined,
  hccPageLabel: string,
  canEditModelSchema: boolean
): DetailRow[] {
  return (simulations ?? []).flatMap((simulation) => {
    if (simulation.id === undefined) return [];

    return [
      {
        id: String(simulation.id),
        name: simulation.name,
        value: getPrettyDateTime(simulation.startDatetime),
        ...(canEditModelSchema && {
          action: <SimulationLink simulationId={simulation.id} label={hccPageLabel} />,
        }),
      },
    ];
  });
}

export function SchematisationSimulationsSection() {
  const { schematisation, revision, threediModel } = useSchematisationDetailsContext();
  const revisionId = revision.id;

  const intl = useIntl();

  const { canEditModelSchema } = useModelSchematisationPermission(schematisation.id);

  const modelUsagesQuery = useHccModelSimulationsUsagesQuery(threediModel?.id);
  const revisionSimulationsQuery = useHccRevisionSimulationsQuery(revisionId);

  const hccPageLabel = intl.formatMessage({
    id: "file.hcc_management_page",
    defaultMessage: "Rana HCC page",
  });

  const modelRows = getModelSimulationRows(
    modelUsagesQuery.data?.results,
    hccPageLabel,
    canEditModelSchema
  );
  const revisionRows = getRevisionSimulationRows(
    revisionSimulationsQuery.data?.results,
    hccPageLabel,
    canEditModelSchema
  );

  const rows = modelRows.length > 0 ? modelRows : revisionRows;
  const isLoading = threediModel?.id
    ? modelUsagesQuery.isLoading ||
      (modelUsagesQuery.isSuccess && revisionSimulationsQuery.isLoading)
    : revisionSimulationsQuery.isLoading;

  if (!isLoading && rows.length === 0) return null;

  return (
    <AnimateSize>
      <HeaderBox
        name={intl.formatMessage({
          id: "section.previous_simulations",
          defaultMessage: "Previously ran simulations",
        })}
        boxHeight={0}
        mb={0}
        borderBottomWidth={0}
        borderBottomRadius="none"
      />
      <DetailTable rows={rows} isLoading={isLoading} />
    </AnimateSize>
  );
}
